import { useI18N } from "common/hooks";
import { getVenueLocationSubtitle, pinTypeForVenueDetails } from "common/utils";
import { RWebShare } from "react-web-share";
import { AuthModalScreen, useAuthModals } from "@/hooks/useAuthModals";
import { Tooltip } from "react-tooltip";
import { BellIcon, BellRingingIcon, ExportIcon, HeartStraightIcon } from "@phosphor-icons/react";
import { ChevronRight } from "lucide-react";
import OpeningHours from "@/components/VenueCard/TodaysOpeningHours";
import { VenueDetails, BusinessPhoto } from "common/types";
import { bucketUrlPrefix } from "common/config";
import { useEffect, useRef, useState } from "react";
import VenuePlaceholderImage from "@/components/common/VenuePlaceholderImage";
import { createPageAnalytics, trackEvent } from "common/analytics";


type VenueImageWithActionsProps = {
  businessPhoto: string;
  businessphotoThumb: string;
  venueName: string;
  venueSlug: string;
  hasVenuePhoto: boolean;
  isVenueSaved: () => boolean;
  handleSaveVenue: () => void;
  isLoggedIn: boolean;
  setActionToPerformAfterLogin: (action: string) => void;
  isDark: boolean;
  onNotificationClick?: () => void;
  isNotificationEnabled: () => boolean;
  venueDetails?: VenueDetails;
};


function getFivePhotos(businessPhotos: BusinessPhoto[]): BusinessPhoto[] {
  // Separate photos by user role priority
  const priorityPhotos: BusinessPhoto[] = []; // isGeneralUser: false
  const generalPhotos: BusinessPhoto[] = [];   // isGeneralUser: true

  // Categorize photos by user role
  for (const photo of businessPhotos) {
    if (photo.user_roles?.isGeneralUser === false) {
      priorityPhotos.push(photo);
    } else {
      generalPhotos.push(photo);
    }
  }

  const uniqueTagPhotosMap = new Map<string, BusinessPhoto>();
  const result: BusinessPhoto[] = [];

  // First, populate with priority photos (isGeneralUser: false) ensuring unique tagNames
  for (const photo of priorityPhotos) {
    if (!uniqueTagPhotosMap.has(photo.tagName)) {
      uniqueTagPhotosMap.set(photo.tagName, photo);
    }
    if (uniqueTagPhotosMap.size >= 5) {
      break;
    }
  }

  // If we still need more photos, add from general photos (isGeneralUser: true)
  if (uniqueTagPhotosMap.size < 5) {
    for (const photo of generalPhotos) {
      if (!uniqueTagPhotosMap.has(photo.tagName)) {
        uniqueTagPhotosMap.set(photo.tagName, photo);
      }
      if (uniqueTagPhotosMap.size >= 5) {
        break;
      }
    }
  }

  // Add unique photos to the result
  const uniquePhotos = Array.from(uniqueTagPhotosMap.values());
  result.push(...uniquePhotos);

  // If we have fewer than 5 unique photos, add more photos without duplicating
  // First try priority photos, then general photos
  const remainingPhotos = [...priorityPhotos, ...generalPhotos];

  for (const photo of remainingPhotos) {
    if (result.length >= 5) {
      break;
    }
    if (!result.includes(photo)) {
      result.push(photo);
    }
  }

  return result;
}

export default function VenueImageWithActions({
  businessPhoto,
  businessphotoThumb,
  venueName,
  venueSlug,
  hasVenuePhoto,
  isVenueSaved,
  handleSaveVenue,
  isLoggedIn,
  setActionToPerformAfterLogin,
  isDark,
  onNotificationClick,
  isNotificationEnabled,
  venueDetails,
}: VenueImageWithActionsProps) {
  const i18n = useI18N();
  const { showModal } = useAuthModals();
  const photoScrollContainerRef = useRef<HTMLDivElement>(null);
  // Current slide of the mobile/tablet hero carousel — drives photo_navigated's index props
  // (the carousel uses scroll-snap, so there is no built-in index to read).
  const heroIndexRef = useRef(0);

  const locationSubtitle = getVenueLocationSubtitle({
    sublocality: venueDetails?.sublocality,
    route: venueDetails?.route,
    postalTown: venueDetails?.postalTown,
    locality: venueDetails?.locality,
    cityLocation: venueDetails?.cityLocation,
    businessType: venueDetails?.businessType,
  });
  const [desktopImageError, setDesktopImageError] = useState(false);
  const [erroredCarouselIndexes, setErroredCarouselIndexes] = useState<Set<number>>(new Set());
  const [shareOrigin, setShareOrigin] = useState("");
  useEffect(() => setShareOrigin(window.location.origin), []);

  const handleSaveClick = () => {
    if (isLoggedIn) {
      handleSaveVenue();
    } else {
      // Set the method in the Atom
      console.log("setActionToPerformAfterLogin in the Atom");
      setActionToPerformAfterLogin("handleSaveVenue");

      // Show login modal
      showModal(AuthModalScreen.EnterEmail);
    }
  };

  const placeAnalytics = createPageAnalytics("place");

  const handleNotificationClickWithTracking = () => {
    placeAnalytics.track("bell_action_button", {
      brand_id: venueDetails?.brandId != null ? String(venueDetails?.brandId) : "",
      brand_name: venueDetails?.brandName ?? "",
      business_id: venueDetails?.business?.id?.toString() ?? "",
      // Canonical pin_type via the shared helper (was businessType — the venue category, not a
      // pin_type value). Matches the other venue-detail place_* emitters.
      pin_type: pinTypeForVenueDetails(venueDetails),
      place_name: venueDetails?.name ?? "",
    });

    if (onNotificationClick) {
      onNotificationClick();
    }
  };

  const handleShareClick = () => {
    // share_place_action_button -> place_share_cta_activated.
    placeAnalytics.track("share_place_action_button", {
      brand_id: venueDetails?.brandId != null ? String(venueDetails?.brandId) : "",
      brand_name: venueDetails?.brandName ?? "",
      business_id: venueDetails?.business?.id?.toString() ?? "",
      place_name: venueDetails?.name ?? "",
    });
  };

  const top5Photos = venueDetails ? getFivePhotos(venueDetails.businessPhotos) : [];

  // hero_photo_carousel_target_area -> photo_navigated. Fired per arrow tap on the mobile/tablet
  // hero carousel; guarded at the edges so it only fires on an actual slide change. photo_view_type
  // "venue_carousel" marks the venue hero (vs the space/lightbox carousels). photo_collection has no
  // hero-specific value in the controlled vocab (walkthrough|experiences|by_users) — "by_users" is a
  // best-effort label for the mixed venue photo pool; revisit if a "venue" collection value is added.
  const trackHeroNavigation = (direction: "previous" | "next", nextIndex: number) => {
    trackEvent("photo_navigated", {
      brand_id: venueDetails?.brandId != null ? String(venueDetails?.brandId) : "",
      brand_name: venueDetails?.brandName ?? "",
      business_id: venueDetails?.business?.id?.toString() ?? "",
      navigation_direction: direction,
      photo_collection: "by_users",
      photo_index: nextIndex,
      photo_view_type: "venue_carousel",
      place_header_name: "",
      place_name: venueDetails?.name ?? "",
      place_space_name: "",
      previous_photo_index: heroIndexRef.current,
    });
    heroIndexRef.current = nextIndex;
  };

  const handlePhotoScrollLeft = () => {
    const container = photoScrollContainerRef.current;
    if (!container) return;
    if (heroIndexRef.current > 0) {
      trackHeroNavigation("previous", heroIndexRef.current - 1);
    }
    container.scrollBy({ left: -container.clientWidth, behavior: "smooth" });
  };

  const handlePhotoScrollRight = () => {
    const container = photoScrollContainerRef.current;
    if (!container) return;
    if (heroIndexRef.current < top5Photos.length - 1) {
      trackHeroNavigation("next", heroIndexRef.current + 1);
    }
    container.scrollBy({ left: container.clientWidth, behavior: "smooth" });
  };

  return (
    <div className="flex flex-col gap-3">

      {/* Desktop View */}
      <div className="hidden lg:block relative aspect-[4/3] rounded-xl overflow-hidden">
        {/* Venue image */}
        {hasVenuePhoto && !desktopImageError ? (
          <img
            src={businessPhoto !== "" ? businessPhoto : businessphotoThumb}
            alt={venueName}
            className="w-full h-full object-cover"
            onError={() => setDesktopImageError(true)}
          />
        ) : (
          <VenuePlaceholderImage className="w-full h-full" />
        )}

       

        {/* Action Buttons Overlay - Hidden on mobile/tablet, shown on desktop */}
        <div className="hidden lg:flex absolute bottom-3 right-3 items-center gap-2 z-20">
          {/* Notification Button */}
          <button
            id="notification-button"
            className="flex items-center justify-center w-8 h-8"
            aria-label={i18n.get("getNotifications")}
            onClick={handleNotificationClickWithTracking}
          >
            <span className="flex items-center justify-center w-8 h-8 
            bg-bg-brand-secondary-default rounded-full 
            hover:bg-bg-brand-ghost-hover 
            active:bg-bg-brand-ghost-active 
            transition-colors border border-border-token-brand-minimal">
              {isNotificationEnabled() ? (
                <BellRingingIcon  size={16} className="text-fg-neutral-strong" weight="fill" />
              ) : (
                <BellIcon size={16} className="text-fg-neutral-strong" weight="regular" />
              )}
            </span>
          </button>

          {/* Save Button */}
          <button
            id="save-button"
            className="flex items-center justify-center w-8 h-8"
            aria-label={isVenueSaved() ? i18n.get("saved") : i18n.get("save")}
            onClick={handleSaveClick}
          >
            <span className="flex items-center justify-center w-8 h-8 bg-bg-brand-secondary-default rounded-full hover:bg-bg-brand-ghost-hover active:bg-bg-brand-ghost-active transition-colors border border-border-token-brand-minimal">
              <HeartStraightIcon
                size={16}
                weight={isVenueSaved() ? "fill" : "regular"}
                aria-hidden="true"
                className={isDark ? "" : "text-fg-neutral-strong"}
              />
            </span>
          </button>

          {/* Share Button */}
          <RWebShare
            data={{
              text: i18n.get("seeThisSociabilityVenueText"),
              url: shareOrigin + `/venuedetails/${venueSlug}`,
              title: i18n.get("seeThisSociabilityVenueTitle"),
            }}
            onClick={handleShareClick}
          >
            <button
              id="share-button"
              className="flex items-center justify-center w-8 h-8"
              aria-label={i18n.get("shareThisVenue")}
            >
              <span className="flex items-center justify-center w-8 h-8 bg-bg-brand-secondary-default rounded-full hover:bg-bg-brand-ghost-hover active:bg-bg-brand-ghost-active transition-colors border border-border-token-brand-minimal">
                <ExportIcon
                  size={16}
                  weight="regular"
                  aria-hidden="true"
                  className={isDark ? "" : "text-fg-neutral-strong"}
                />
              </span>
            </button>
          </RWebShare>
        </div>

        {/* Tooltips */}
        <Tooltip
          anchorSelect="#notification-button"
          content={i18n.get("getNotifications")}
          place="bottom"
          style={{ zIndex: 9999 }}
        />
        <Tooltip
          anchorSelect="#share-button"
          content={i18n.get("shareThisVenue")}
          place="bottom"
          style={{ zIndex: 9999 }}
        />
        <Tooltip
          anchorSelect="#save-button"
          content={isVenueSaved() ? i18n.get("saved") : i18n.get("save")}
          place="bottom"
          style={{ zIndex: 9999 }}
        />
      </div>

      {/* Venue InfoIcon - Show on mobile only */}
      {venueDetails && (
        <>
          {/* show the top 5 photos as carousel or placeholder */}
          {venueDetails?.businessPhotos?.length > 0 && hasVenuePhoto ? (
            <div className="block lg:hidden relative min-w-0 mb-2">
              {/* Carousel Navigation Buttons */}
              <button
                type="button"
                onClick={handlePhotoScrollLeft}
                className="flex absolute left-3 top-1/2 -translate-y-1/2 z-10 items-center justify-center w-10 h-10 bg-container-brand-minimal border border-border-token-brand-minimal rounded-full hover:bg-bg-brand-ghost-hover transition-all focus-blue shadow-md hover:shadow-lg"
                aria-label={i18n.get("scrollLeft")}
              >
                <ChevronRight
                  size={18}
                  aria-hidden="true"
                  className="rotate-180 text-fg-neutral-strong"
                />
              </button>

              <button
                type="button"
                onClick={handlePhotoScrollRight}
                className="flex absolute right-3 top-1/2 -translate-y-1/2 z-10 items-center justify-center w-10 h-10 bg-container-brand-minimal border border-border-token-brand-minimal rounded-full hover:bg-bg-brand-ghost-hover transition-all focus-blue shadow-md hover:shadow-lg"
                aria-label={i18n.get("scrollRight")}
              >
                <ChevronRight size={18} aria-hidden="true" className="text-fg-neutral-strong" />
              </button>

              {/* Photo Cards Carousel - Wrapper to contain negative margins */}
              <div className="overflow-hidden min-w-0">
                <div
                  ref={photoScrollContainerRef}
                  className="flex gap-3 overflow-x-auto scrollbar-hide snap-x snap-mandatory min-w-0"
                  role="list"
                  aria-label={i18n.get("venuePhotos")}
                >
                  {top5Photos.map((photo, index) => (
                    <div
                      key={`photo-${index}`}
                      className="flex-shrink-0 w-full snap-center"
                    >
                      <div className="relative w-full aspect-[4/3] rounded-xl overflow-hidden">
                        {erroredCarouselIndexes.has(index) ? (
                          <VenuePlaceholderImage className="w-full h-full" />
                        ) : (
                          <img
                            src={bucketUrlPrefix + photo.path}
                            alt={photo.tagName}
                            className="w-full h-full object-cover"
                            onError={() =>
                              setErroredCarouselIndexes((prev) => new Set(prev).add(index))
                            }
                          />
                        )}

                        <div className="max-w-[80%] px-3 py-1 w-max absolute bottom-3 right-3 opacity-75 rounded-full text-fg-neutral-onFilled label-s-medium bg-surface-modal">
                          {photo.tagName} • {index + 1}/{top5Photos.length}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="block lg:hidden relative w-full aspect-[4/3] rounded-xl overflow-hidden mb-2">
              <VenuePlaceholderImage className="w-full h-full" />
            </div>
          )}


          <div className="lg:hidden flex flex-col gap-1">
            {/* Title and Action Buttons Row */}
            <div className="flex items-start justify-between gap-2">

              <div className="flex flex-col gap-2">
                {/* Group 1: Name + Location (tightly grouped) */}
                <div className="flex flex-col gap-1">
                  <h1 className="label-l-bold text-fg-neutral-strong flex-1">
                    {venueDetails.name}
                  </h1>
                  {locationSubtitle && (
                    <p className="label-s-medium text-fg-neutral-subtle">
                      {locationSubtitle}
                    </p>
                  )}
                </div>

                {/* Meta InfoIcon (Distance, Hours) */}
                <p className="label-s-medium text-fg-neutral-subtle">
                  <span>{venueDetails.distance} mile</span>
                  {/* Opening Hours (arrow focusable, if present) */}
                  {venueDetails.openingHours &&
                    venueDetails.openingHours[0]?.mode === 0 && (
                      <>
                        <span className="text-fg-neutral-minimal"> | </span>
                        <OpeningHours
                          openingHours={venueDetails.openingHours}
                          containerClassName=""
                          className="label-s-medium text-fg-neutral-subtle"
                        />
                      </>
                    )}
                </p>

                {/* Sociability Verified */}
                {venueDetails?.businessVerified != null && (
                  <div className="flex items-center gap-1">
                    <img src="/images/SealCheck.svg" alt="" aria-hidden="true" className="w-5 h-5 shrink-0" />
                    <span className="label-m-semibold text-fg-neutral-subtle whitespace-nowrap">
                      {venueDetails.businessVerified}
                    </span>
                    {venueDetails.businessVerifiedDatetime && (
                      <span className="label-s-medium text-fg-neutral-subtle whitespace-nowrap">
                        {`: ${new Date(venueDetails.businessVerifiedDatetime.replace(" ", "T")).toLocaleDateString("en-GB", { month: "long", year: "numeric" })}`}
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* Action Buttons - Mobile only */}
              <div className="flex items-center gap-2 flex-shrink-0">
                {/* Notification Button */}
                <button
                  id="notification-button-mobile"
                  className="flex items-center justify-center w-8 h-8"
                  aria-label={i18n.get("getNotifications")}
                  onClick={handleNotificationClickWithTracking}
                >
                  <span className="flex items-center justify-center w-8 h-8 bg-bg-brand-secondary-default rounded-full hover:bg-bg-brand-ghost-hover active:bg-bg-brand-ghost-active transition-colors border border-border-token-brand-minimal">
                    {isNotificationEnabled() ? (
                      <BellRingingIcon size={16} className="text-fg-neutral-strong" weight="fill" />
                    ) : (
                      <BellIcon size={16} className="text-fg-neutral-strong" weight="regular" />
                    )}
                  </span>
                </button>

                {/* Save Button */}
                <button
                  id="save-button-mobile"
                  className="flex items-center justify-center w-8 h-8"
                  aria-label={isVenueSaved() ? i18n.get("saved") : i18n.get("save")}
                  onClick={handleSaveClick}
                >
                  <span className="flex items-center justify-center w-8 h-8 bg-bg-brand-secondary-default rounded-full hover:bg-bg-brand-ghost-hover active:bg-bg-brand-ghost-active transition-colors border border-border-token-brand-minimal">
                    <HeartStraightIcon
                      size={16}
                      weight={isVenueSaved() ? "fill" : "regular"}
                      aria-hidden="true"
                      className={isDark ? "" : "text-fg-neutral-strong"}
                    />
                  </span>
                </button>

                {/* Share Button */}
                <RWebShare
                  data={{
                    text: i18n.get("seeThisSociabilityVenueText"),
                    url: shareOrigin + `/venuedetails/${venueSlug}`,
                    title: i18n.get("seeThisSociabilityVenueTitle"),
                  }}
                  onClick={handleShareClick}
                >
                  <button
                    id="share-button-mobile"
                    className="flex items-center justify-center w-8 h-8"
                    aria-label={i18n.get("shareThisVenue")}
                  >
                    <span className="flex items-center justify-center w-8 h-8 bg-bg-brand-secondary-default rounded-full hover:bg-bg-brand-ghost-hover active:bg-bg-brand-ghost-active transition-colors border border-border-token-brand-minimal">
                      <ExportIcon
                        size={16}
                        weight="regular"
                        aria-hidden="true"
                        className={isDark ? "" : "text-fg-neutral-strong"}
                      />
                    </span>
                  </button>
                </RWebShare>
              </div>
            </div>



            {/* Tooltips for Mobile Buttons */}
            <Tooltip
              anchorSelect="#notification-button-mobile"
              content={i18n.get("getNotifications")}
              place="bottom"
              style={{ zIndex: 9999 }}
            />
            <Tooltip
              anchorSelect="#share-button-mobile"
              content={i18n.get("shareThisVenue")}
              place="bottom"
              style={{ zIndex: 9999 }}
            />
            <Tooltip
              anchorSelect="#save-button-mobile"
              content={isVenueSaved() ? i18n.get("saved") : i18n.get("save")}
              place="bottom"
              style={{ zIndex: 9999 }}
            />
          </div>
        </>
      )}
    </div>
  );
}
